export type EntityType =
  | 'character'
  | 'location'
  | 'manual_chapter'
  | 'storyline'
  | 'scene'
  | 'resource'
  | 'timeline_item'
  | 'document';

export interface RelatedEntity {
  type: string;
  id: string;
  documentId?: string;
  label: string;
}

export const ENTITY_TYPE_LABEL_KEYS: Record<EntityType, string> = {
  character: 'characters',
  location: 'locations',
  manual_chapter: 'chapters',
  storyline: 'storylines',
  scene: 'scenes',
  resource: 'Resources',
  timeline_item: 'Timeline',
  document: 'Documents',
};

export function removeMentionFromHtml(
  html: string,
  entity: { type: string; id: string; documentId?: string }
): string {
  const mentionSpanRegex = /<span[^>]*data-type="mention"[^>]*>[\s\S]*?<\/span>/gi;
  return html.replace(mentionSpanRegex, (match) => {
    const openTagMatch = match.match(/^<span[^>]*>/);
    const openTag = openTagMatch ? openTagMatch[0] : '';
    const idMatch = openTag.match(/data-id="([^"]*)"/);
    const typeMatch = openTag.match(/data-entity-type="([^"]*)"/);
    const docIdMatch = openTag.match(/data-document-id="([^"]*)"/);
    const id = idMatch ? idMatch[1] : '';
    const type = typeMatch ? typeMatch[1] : '';
    const docId = docIdMatch ? docIdMatch[1] : undefined;
    const matchesId = id === entity.id;
    const matchesType = type === entity.type;
    const matchesDocId =
      (entity.documentId == null && docId == null) ||
      (entity.documentId != null && docId != null && entity.documentId === docId);
    if (matchesId && matchesType && matchesDocId) {
      return '';
    }
    return match;
  });
}

export function entityToHref(entity: RelatedEntity, projectId: string): string {
  if (!projectId) return '#';
  const docId = entity.documentId;
  switch (entity.type) {
    case 'character':
      return docId ? `/projects/${projectId}/documents/${docId}?panel=characters&characterId=${entity.id}` : '#';
    case 'location':
      return docId ? `/projects/${projectId}/documents/${docId}?panel=locations&locationId=${entity.id}` : '#';
    case 'manual_chapter':
      return docId ? `/projects/${projectId}/documents/${docId}/chapters?chapterId=${entity.id}` : '#';
    case 'storyline':
      return docId ? `/projects/${projectId}/documents/${docId}?panel=storylines&storylineId=${entity.id}` : '#';
    case 'scene':
      return docId ? `/projects/${projectId}/documents/${docId}?panel=scenes&sceneId=${entity.id}` : '#';
    case 'resource':
      return `/projects/${projectId}/resources/${entity.id}`;
    case 'timeline_item':
      return `/projects/${projectId}/timeline?timelineItemId=${encodeURIComponent(entity.id)}`;
    case 'document':
      return `/projects/${projectId}/documents/${entity.id}`;
    case 'user':
      return `/projects/${projectId}/sharing`;
    default:
      return '#';
  }
}

export function entityDetailHref(
  entity: Pick<RelatedEntity, 'type' | 'id' | 'documentId' | 'label'>,
  projectId: string | undefined
): string | undefined {
  if (!projectId) {
    return undefined;
  }
  const href = entityToHref(entity, projectId);
  return href === '#' ? undefined : href;
}

const MENTION_CLICK_TOOLTIP_KEYS: Record<string, string> = {
  character: 'character_tag_click_to_edit',
  location: 'location_tag_click_to_edit',
  manual_chapter: 'mention_click_to_open_chapter',
  storyline: 'mention_click_to_open_storyline',
  scene: 'mention_click_to_open_scene',
  resource: 'mention_click_to_open_resource',
  timeline_item: 'mention_click_to_open_timeline_item',
  document: 'mention_click_to_open_document',
  user: 'mention_click_to_open_sharing',
};

const MENTION_IDENTITY_TOOLTIP_KEYS: Record<string, string> = {
  user: 'mention_is_shared_user',
  character: 'mention_is_character',
  location: 'mention_is_location',
  manual_chapter: 'mention_is_chapter',
  storyline: 'mention_is_storyline',
  scene: 'mention_is_scene',
  resource: 'mention_is_resource',
  timeline_item: 'mention_is_timeline_item',
  document: 'mention_is_document',
};

export function mentionTooltipKey(
  entity: Pick<RelatedEntity, 'type' | 'id' | 'documentId' | 'label'>,
  projectId: string | undefined
): string | null {
  if (entityDetailHref(entity, projectId)) {
    return MENTION_CLICK_TOOLTIP_KEYS[entity.type] ?? null;
  }
  return MENTION_IDENTITY_TOOLTIP_KEYS[entity.type] ?? null;
}

const MENTION_NODE_SELECTOR =
  '.mention[data-id], [data-type="mention"][data-id], [data-type="scriptMention"][data-id], [data-script-mention][data-id]';
const MENTION_IGNORE_SELECTOR = '[data-mention-list], [data-script-mention-list], [data-resource-linked-entities]';

export function relatedEntityFromMentionElement(mention: Element): RelatedEntity | null {
  const id = mention.getAttribute('data-id');
  const type = mention.getAttribute('data-entity-type');
  if (!id || !type) {
    return null;
  }
  const documentId = mention.getAttribute('data-document-id') || undefined;
  const label = mention.getAttribute('data-label') ?? mention.textContent?.replace(/^@/, '') ?? '';
  return { id, type, documentId, label };
}

export function mentionElementFromEvent(event: Event): HTMLElement | null {
  const target = event.target;
  if (!(target instanceof Element)) {
    return null;
  }
  if (target.closest(MENTION_IGNORE_SELECTOR)) {
    return null;
  }
  const mention = target.closest(MENTION_NODE_SELECTOR);
  return mention instanceof HTMLElement ? mention : null;
}

export function relatedEntityFromMentionEvent(event: Event): RelatedEntity | null {
  const mention = mentionElementFromEvent(event);
  if (!mention) {
    return null;
  }
  return relatedEntityFromMentionElement(mention);
}
